/**
 * NTISP device heartbeat: registers this device with the Security Center on
 * sign-in so trusted devices, device recognition, login history, and
 * new-device detection run on real data.
 *
 * The fingerprint is a soft signal (user agent, platform, screen class,
 * timezone, language), hashed server-side; per the mandate it is a RISK
 * signal, never sole identity. Fire-and-forget: a heartbeat failure never
 * affects the session.
 */

import { authHeadersJson } from "@/api/authToken";

let sentThisSession = false;

function computeFingerprint(): string {
  const n = navigator;
  const parts = [
    n.userAgent,
    n.platform ?? "",
    n.language ?? "",
    String(screen?.width ?? 0),
    String(screen?.height ?? 0),
    Intl.DateTimeFormat().resolvedOptions().timeZone ?? "",
    String(n.hardwareConcurrency ?? 0),
  ];
  return parts.join("|");
}

function detectBrowser(): string {
  const ua = navigator.userAgent;
  const m = ua.match(/(Edg|OPR|Chrome|Firefox|Safari)\/[\d.]+/);
  return m ? m[0] : "Browser";
}

function detectPlatform(): string {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return "Android";
  if (/iphone|ipad|ipod/i.test(ua)) return "iOS";
  if (/mac/i.test(ua)) return "macOS";
  if (/win/i.test(ua)) return "Windows";
  if (/linux/i.test(ua)) return "Linux";
  return "Unknown";
}

/** Send at most one heartbeat per page session, on sign-in. */
export function sendDeviceHeartbeat(): void {
  if (sentThisSession || typeof window === "undefined") return;
  sentThisSession = true;
  void (async () => {
    try {
      const headers = await authHeadersJson();
      if (!headers.Authorization) return;
      await fetch("/api/security/device/heartbeat", {
        method: "POST",
        headers,
        body: JSON.stringify({
          fingerprint: computeFingerprint(),
          platform: detectPlatform(),
          browser: detectBrowser(),
        }),
      });
    } catch { /* fire-and-forget; the session is unaffected */ }
  })();
}
